/**
 * SoundControlsPanel
 *
 * A reusable component that displays a speaker icon in the bottom right corner.
 * Clicking the icon opens a menu with sound control options.
 */

export interface SoundSettings {
    muteAll: boolean;
    cardEffects: boolean;
    slotEffects: boolean;
    texasHoldemVoices: boolean;
}

export class SoundControlsPanel {
    private scene: Phaser.Scene;
    private container: Phaser.GameObjects.Container | null = null;
    private menuContainer: Phaser.GameObjects.Container | null = null;
    private isMenuOpen: boolean = false;

    // Sound settings - all enabled by default
    private settings: SoundSettings = {
        muteAll: false,
        cardEffects: true,
        slotEffects: true,
        texasHoldemVoices: true
    };

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        this.loadSettings();
        this.createSpeakerIcon();
    }

    private loadSettings(): void {
        // Load settings from localStorage
        const savedSettings = localStorage.getItem('soundSettings');
        if (savedSettings) {
            try {
                this.settings = JSON.parse(savedSettings);
            } catch (e) {
                console.warn('Failed to load sound settings:', e);
            }
        }
    }

    private saveSettings(): void {
        // Save settings to localStorage
        localStorage.setItem('soundSettings', JSON.stringify(this.settings));
        console.log('Sound settings saved:', this.settings);
    }

    private createSpeakerIcon(): void {
        const { width, height } = this.scene.cameras.main;

        // Position in bottom right corner
        const iconX = width - 40;
        const iconY = height - 40;

        this.container = this.scene.add.container(iconX, iconY);

        // Background circle for the icon
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x000000, 0.8);
        bg.fillCircle(0, 0, 28);
        bg.lineStyle(2, 0x00FFFF, 1);
        bg.strokeCircle(0, 0, 28);

        // Draw speaker icon using graphics
        const speakerIcon = this.scene.add.graphics();
        this.drawSpeakerIcon(speakerIcon);

        this.container.add([bg, speakerIcon]);
        this.container.setInteractive(
            new Phaser.Geom.Circle(0, 0, 28),
            Phaser.Geom.Circle.Contains
        );

        // Set depth to ensure it's above other elements
        this.container.setDepth(10000);

        // Hover effects
        this.container.on('pointerover', () => {
            this.container!.setScale(1.1);
            bg.clear();
            bg.fillStyle(0x0066FF, 0.9);
            bg.fillCircle(0, 0, 28);
            bg.lineStyle(2, 0xFFFFFF, 1);
            bg.strokeCircle(0, 0, 28);
        });

        this.container.on('pointerout', () => {
            this.container!.setScale(1.0);
            bg.clear();
            bg.fillStyle(0x000000, 0.8);
            bg.fillCircle(0, 0, 28);
            bg.lineStyle(2, 0x00FFFF, 1);
            bg.strokeCircle(0, 0, 28);
        });

        // Click to toggle menu
        this.container.on('pointerdown', () => {
            if (this.isMenuOpen) {
                this.closeMenu();
            } else {
                this.openMenu();
            }
        });
    }

    private drawSpeakerIcon(graphics: Phaser.GameObjects.Graphics): void {
        graphics.clear();

        // Check if muted
        if (this.settings.muteAll) {
            // Draw muted speaker (with X)
            graphics.fillStyle(0xFF0000, 1);
        } else {
            // Draw normal speaker
            graphics.fillStyle(0xFFFFFF, 1);
        }

        // Speaker cone (trapezoid)
        graphics.beginPath();
        graphics.moveTo(-10, -6);
        graphics.lineTo(-10, 6);
        graphics.lineTo(-4, 10);
        graphics.lineTo(-4, -10);
        graphics.closePath();
        graphics.fillPath();

        // Speaker box
        graphics.fillRoundedRect(-14, -4, 4, 8, 1);

        if (this.settings.muteAll) {
            // Draw X for muted
            graphics.lineStyle(2, 0xFF0000, 1);
            graphics.beginPath();
            graphics.moveTo(0, -8);
            graphics.lineTo(10, 8);
            graphics.moveTo(0, 8);
            graphics.lineTo(10, -8);
            graphics.strokePath();
        } else {
            // Draw sound waves
            graphics.lineStyle(2, 0xFFFFFF, 1);

            // Small wave
            graphics.beginPath();
            graphics.arc(0, 0, 6, -Math.PI / 4, Math.PI / 4);
            graphics.strokePath();

            // Medium wave
            graphics.beginPath();
            graphics.arc(0, 0, 10, -Math.PI / 3, Math.PI / 3);
            graphics.strokePath();
        }
    }

    private openMenu(): void {
        if (this.isMenuOpen) return;

        const { width, height } = this.scene.cameras.main;

        // Position menu above and to the left of the icon
        const menuX = width - 220;
        const menuY = height - 260;

        this.menuContainer = this.scene.add.container(menuX, menuY);
        this.menuContainer.setDepth(10001);

        // Menu background
        const menuBg = this.scene.add.graphics();
        menuBg.fillStyle(0x001122, 0.95);
        menuBg.fillRoundedRect(0, 0, 240, 220, 8);
        menuBg.lineStyle(3, 0x00FFFF, 1);
        menuBg.strokeRoundedRect(0, 0, 240, 220, 8);

        // Title
        const title = this.scene.add.text(120, 20, 'SOUND CONTROLS', {
            fontFamily: 'Arial',
            fontSize: '16px',
            color: '#00FFFF',
            fontStyle: 'bold'
        });
        title.setOrigin(0.5, 0);

        this.menuContainer.add([menuBg, title]);

        // Create checkboxes
        const options = [
            { key: 'muteAll', label: 'Mute All Sounds', y: 60 },
            { key: 'cardEffects', label: 'Card Effects', y: 100 },
            { key: 'slotEffects', label: 'Slot Effects', y: 140 },
            { key: 'texasHoldemVoices', label: 'Texas Hold \'Em Voices', y: 180 }
        ];

        options.forEach(option => {
            this.createCheckbox(option.key as keyof SoundSettings, option.label, 15, option.y);
        });

        this.isMenuOpen = true;
    }

    private createCheckbox(key: keyof SoundSettings, label: string, x: number, y: number): void {
        if (!this.menuContainer) return;

        const isChecked = this.settings[key];

        // Checkbox background
        const checkboxBg = this.scene.add.graphics();
        checkboxBg.fillStyle(0xFFFFFF, 1);
        checkboxBg.fillRect(x, y, 16, 16);
        checkboxBg.lineStyle(2, 0x000000, 1);
        checkboxBg.strokeRect(x, y, 16, 16);

        // Checkmark (if checked)
        const checkmark = this.scene.add.graphics();
        if (isChecked) {
            checkmark.lineStyle(2, 0x00FF00, 1);
            checkmark.beginPath();
            checkmark.moveTo(x + 3, y + 8);
            checkmark.lineTo(x + 7, y + 12);
            checkmark.lineTo(x + 13, y + 4);
            checkmark.strokePath();
        }

        // Label text
        const labelText = this.scene.add.text(x + 22, y + 2, label, {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#FFFFFF'
        });

        // Create an interactive zone covering the entire row
        // Zone position is at its center, so we need to position it at the middle of the clickable area
        const zone = this.scene.add.zone(x + 110, y + 8, 220, 30);
        zone.setInteractive();

        // Click handler
        zone.on('pointerdown', () => {
            this.toggleSetting(key);
            this.closeMenu();
            this.openMenu(); // Reopen to refresh checkboxes
        });

        // Hover effect
        zone.on('pointerover', () => {
            labelText.setColor('#FFFF00');
        });

        zone.on('pointerout', () => {
            labelText.setColor('#FFFFFF');
        });

        this.menuContainer!.add([checkboxBg, checkmark, labelText, zone]);
    }

    private toggleSetting(key: keyof SoundSettings): void {
        this.settings[key] = !this.settings[key];

        // If muteAll is toggled, we might want to update the icon
        if (key === 'muteAll') {
            this.updateSpeakerIcon();
        }

        this.saveSettings();
        console.log(`Sound setting toggled: ${key} = ${this.settings[key]}`);
    }

    private updateSpeakerIcon(): void {
        if (!this.container) return;

        // Update the speaker icon graphics
        const speakerIcon = this.container.list[1] as Phaser.GameObjects.Graphics;
        if (speakerIcon) {
            this.drawSpeakerIcon(speakerIcon);
        }
    }

    private closeMenu(): void {
        if (!this.isMenuOpen || !this.menuContainer) return;

        this.menuContainer.destroy();
        this.menuContainer = null;
        this.isMenuOpen = false;
    }

    public getSettings(): SoundSettings {
        return { ...this.settings };
    }

    public isMuted(): boolean {
        return this.settings.muteAll;
    }

    public isCardEffectsEnabled(): boolean {
        return this.settings.cardEffects && !this.settings.muteAll;
    }

    public isSlotEffectsEnabled(): boolean {
        return this.settings.slotEffects && !this.settings.muteAll;
    }

    public isTexasHoldemVoicesEnabled(): boolean {
        return this.settings.texasHoldemVoices && !this.settings.muteAll;
    }

    public destroy(): void {
        if (this.menuContainer) {
            this.menuContainer.destroy();
            this.menuContainer = null;
        }
        if (this.container) {
            this.container.destroy();
            this.container = null;
        }
    }
}
